import { Game } from './Game';
import { loadCatalogue } from './game/buildings/Catalogue';
import { loadMaterials } from './game/buildings/Materials';
import { join, logOut, storedSession, forget, type Joined, type Session } from './network/Session';
import { loadSummary } from './network/Summary';
import { hideLoader, showLoader } from './ui/Loader';
import { requestSession } from './ui/LoginOverlay';
import { showPlayerPage } from './ui/PlayerPage';
import './ui/overlay.css';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Getting into the world.
 *
 * Four steps, in this order and not another: a session, the player's page, a
 * place to stand, then the game. Terrain is streamed around wherever the player
 * is, so starting the renderer before the server has said where that is would
 * mean fetching the wrong ground and throwing it away.
 *
 * A stored token skips the first step. It can still be refused — expired, or
 * the account is gone — and then the login screen appears with the reason on
 * it rather than a blank failure.
 *
 * **The page is not a formality.** Logging in used to walk straight into the
 * world, and there was then nowhere to put somebody the server had shown out
 * for half an hour of doing nothing: the client would have walked them back in
 * within the second. The page is that somewhere, and it is the same one after a
 * login and after an idle exit — see `ui/PlayerPage`.
 */

/** A session and a position, asking for credentials as many times as it takes. */
async function enter(container: HTMLElement): Promise<{ session: Session; joined: Joined }> {
  let session = storedSession();
  let notice: string | undefined;

  for (;;) {
    // A stored session can be one that never got as far as choosing a name —
    // the tab was closed on that screen. The overlay picks up where it left off
    // rather than the world refusing entry to somebody who cannot see why.
    if (!session || session.needsNickname) {
      hideLoader();
      session = await requestSession(container, notice);
    }

    try {
      // The dictionaries come along because the page names buildings and goods,
      // and they are the same two files the game loads a moment later — the
      // browser serves the second read out of its cache.
      hideLoader();
      const [summary, catalogue, materials] = await Promise.all([
        loadSummary(session.token),
        loadCatalogue(API),
        loadMaterials(API),
      ]);

      const choice = await showPlayerPage(container, summary, { catalogue, materials }, session.token);

      if (choice === 'logout') {
        await logOut(session);
        // Reloading rather than falling through to the login screen: everything
        // this tab has read belongs to the account that just left it.
        window.location.reload();
        // The reload is not instant, and nothing below should run in the gap.
        await new Promise(() => {});
      }

      showLoader('wchodzę do świata…');
      return { session, joined: await join(session) };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);

      // 401 means the token is no good; anything else — no world on the server,
      // no database, a network that is down — is not something a different
      // password fixes, so the message is shown and the same token tried again.
      if (message.startsWith('401')) {
        forget();
        session = null;
        notice = 'sesja wygasła, zaloguj się ponownie';
      } else if (message.startsWith('403')) {
        // Blocked or disabled: the token is fine and a different password
        // changes nothing, so the account is dropped rather than retried.
        forget();
        session = null;
        notice = message.replace(/^403\s*/, '');
      } else {
        session = null;
        notice = message;
      }
    }
  }
}

async function init(): Promise<void> {
  const container = document.getElementById('game-container');
  if (!container) {
    throw new Error('Game container not found');
  }

  const { session, joined } = await enter(container);
  console.log(
    `🚪 ${joined.username} wchodzi do świata ${joined.world.id} ` +
      `na polu (${joined.position.q}, ${joined.position.r})` +
      (joined.fresh ? ' — po raz pierwszy' : '')
  );

  // Back up for the last and longest wait, whether or not the login screen
  // took it down on the way in. What follows is the terrain arriving and the
  // fog parting over it, and that is the stretch the loader used to sit out:
  // it came down here, which is precisely when the fog closes.
  showLoader('ładowanie świata');

  const game = new Game(container, session, joined);
  await game.initialize();

  // Not taken down here. The game does it, when the world is actually visible
  // behind it — see `Game.stage`.
  game.start();

  window.addEventListener('resize', () => game.onResize());
  window.addEventListener('beforeunload', () => game.dispose());
}

/**
 * Anything that gets this far has already survived the login loop, so a failure
 * here is the game itself refusing to start — a missing WebGL context, a chunk
 * request that will not answer. The loader is taken down either way: leaving a
 * spinner turning over a permanent failure is the one response that tells the
 * player nothing.
 */
function start(): void {
  init().catch((error) => {
    console.error('nie udało się uruchomić gry:', error);
    hideLoader();

    const container = document.getElementById('game-container');
    if (container) {
      const message = document.createElement('p');
      message.className = 'fatal';
      message.textContent = `gra się nie uruchomiła: ${
        error instanceof Error ? error.message : String(error)
      }`;
      container.appendChild(message);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
